import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {AbstractComponent} from "../abstract.component";
import {
  Competitor,
  CompetitorSaveControlPointResultStatus,
  ControlPointTime,
  Lookups,
  RaceSettings,
  ResultProcessingType,
  WaypointType
} from "@model";
import {TranslocoService} from "@ngneat/transloco";
import {debounceTime} from 'rxjs';
import {required} from "@utility";

interface MemberSection {
  member?: Competitor;
  controlTimes: ControlPointTime[];
  expanded: boolean;
}

@Component({
  selector: 'edit-competitor-measurement-times-modal',
  templateUrl: './edit-competitor-measurement-times-modal.component.html',
  styleUrls: ['./edit-competitor-measurement-times-modal.component.scss'],
  standalone: false
})
export class EditCompetitorMeasurementTimes extends AbstractComponent implements OnInit {
  @Input({ required: true }) title: string = "";
  @Input() confirmButtonTitle?: string;
  @Input({ required: true }) competitor?: Competitor;
  @Input({ required: true }) forTeamMembers: boolean = false;
  @Input({ required: true }) languages: Lookups[] = [];
  @Input() raceSettings: RaceSettings | undefined;

  @Output() onSave = new EventEmitter<Competitor>();

  private _saveResult: Map<number, CompetitorSaveControlPointResultStatus> = new Map();
  startDate: Date | undefined;
  finishDate: Date | undefined;
  saveEnabled: boolean = false;
  isFinishWaypoint: boolean = false;
  displayDistanceInput: boolean = false;

  sections: MemberSection[] = [];

  selectedSection?: MemberSection;
  selectedControlTime?: ControlPointTime;
  applyRejected = false;

  decidingRowsByWaypointId = new Map<number, ControlPointTime>();
  private applyChanges = false;
  private sectionValidity = new Map<MemberSection, boolean>();

  @Input()
  set saveResult(value: Map<number, CompetitorSaveControlPointResultStatus> | undefined) {
    if (value && this.mapHasChanged(value)) {
      this._saveResult = new Map(value);
    }
  }

  get saveResult(): Map<number, CompetitorSaveControlPointResultStatus> {
    return this._saveResult;
  }


  inputForm = new FormGroup({
    time: new FormControl(),
    chipTime: new FormControl(),
    penaltyMinutes: new FormControl(),
    lapDistancePassed: new FormControl(),
  });

  constructor(public activeModal: NgbActiveModal, public formBuilder: FormBuilder, public translocoService: TranslocoService) {
    super();
  }

  ngOnInit(): void {
    this.sections = this.forTeamMembers
        ? (this.competitor?.teamMembers ?? []).map(member => ({
          member,
          controlTimes: this.buildControlTimes(member),
          expanded: true,
        }))
        : [{
          member: undefined,
          controlTimes: this.buildControlTimes(this.competitor),
          expanded: true,
        }];

    if (this.raceSettings?.lapLength) {
      this.inputForm.controls.lapDistancePassed.addValidators([Validators.max(this.raceSettings!.lapLength!)])
    }

    this.disableFormControls();
    this.sections.forEach(section => this.validateSection(section));
    this.updateSaveEnabled();
    this.recomputeDecidingRows();
    this.subscriptions.push(
        this.inputForm.controls.time.valueChanges.pipe(debounceTime(300)).subscribe(() => this.onFieldChange()),
        this.inputForm.controls.chipTime.valueChanges.pipe(debounceTime(300)).subscribe(() => this.onFieldChange()),
        this.inputForm.controls.penaltyMinutes.valueChanges.pipe(debounceTime(300)).subscribe(() => this.onFieldChange()),
        this.inputForm.controls.lapDistancePassed.valueChanges.pipe(debounceTime(300)).subscribe(() => this.onFieldChange()),
    );
  }

  private buildControlTimes(source: Competitor | undefined): ControlPointTime[] {
    return source?.controlPointsTimes?.map(item => ({
      id: item.id ?? -1,
      time: item.time ? new Date(item.time) : undefined,
      chipTime: item.chipTime ? new Date(item.chipTime) : undefined,
      controlWaypoint: item.controlWaypoint,
      lap: item.lap,
      penaltyMinutes: item.penaltyMinutes,
      lapDistancePassed: item.lapDistancePassed
    })) || [];
  }

  private byWaypointType(times: ControlPointTime[], type: WaypointType): ControlPointTime | undefined {
    return times.find(cpt => cpt.controlWaypoint?.waypointTypeId === type);
  }

  toggleSection(section: MemberSection): void {
    section.expanded = !section.expanded;
  }

  private recomputeDecidingRows(): void {
    this.decidingRowsByWaypointId.clear();
    if (!this.forTeamMembers) return;

    for (const section of this.sections) {
      for (const controlTime of section.controlTimes) {
        const waypointId = controlTime.controlWaypoint?.id;
        if (!waypointId || !controlTime.time) continue;

        const status = controlTime.id ? this._saveResult.get(controlTime.id) : undefined;
        if (status && status !== CompetitorSaveControlPointResultStatus.OK) continue;

        const isStart = controlTime.controlWaypoint?.waypointTypeId === WaypointType.START_WAYPOINT;
        const current = this.decidingRowsByWaypointId.get(waypointId);

        const isBetter = !current?.time
            ? true
            : isStart
                ? controlTime.time.getTime() < current.time.getTime()
                : controlTime.time.getTime() > current.time.getTime();

        if (isBetter) {
          this.decidingRowsByWaypointId.set(waypointId, controlTime);
        }
      }
    }
  }

  private onFieldChange(): void {
    // Ignore value changes caused by loadFormControls' own patchValue when
    if (!this.applyChanges) return;
    this.onApply();
  }

  onControlTimeRowSelect(section: MemberSection, controlTime: ControlPointTime): void {
    if (controlTime.id === -1) return;
    this.selectedSection = section;
    this.selectedControlTime = controlTime;
    this.applyRejected = false;
    this.loadFormControls(section.controlTimes, controlTime);
  }

  private loadFormControls(times: ControlPointTime[], controlTime: ControlPointTime): void {
    const waypointType = controlTime.controlWaypoint?.waypointTypeId;

    this.startDate = waypointType === WaypointType.START_WAYPOINT
        ? undefined
        : this.byWaypointType(times, WaypointType.START_WAYPOINT)?.time;

    this.isFinishWaypoint = waypointType === WaypointType.FINISH_WAYPOINT;
    this.finishDate = this.isFinishWaypoint
        ? undefined
        : this.byWaypointType(times, WaypointType.FINISH_WAYPOINT)?.time;

    if (this.raceSettings?.resultProcessingType === ResultProcessingType.TIME_LIMITED) {
      if (controlTime.lapDistancePassed) {
        this.displayDistanceInput = true;
      } else {
        const lapControlTimes = times.filter(cpt => cpt.lap != null && cpt.lap > 0);
        const isLastLap = lapControlTimes[lapControlTimes.length - 1] === controlTime;
        this.displayDistanceInput = isLastLap && !lapControlTimes.some(cpt => cpt.lapDistancePassed != null && cpt.lapDistancePassed > 0);
      }
    } else {
      this.displayDistanceInput = false;
    }
    if (this.isFinishWaypoint) {
      this.inputForm.controls.penaltyMinutes.setValidators([Validators.min(0), Validators.max(60)]);
    } else {
      this.inputForm.controls.penaltyMinutes.removeValidators([Validators.min(0), Validators.max(60)]);
    }

    if (controlTime.time) {
      this.inputForm.controls.time.addValidators([required]);
    }
    else {
      this.inputForm.controls.time.removeValidators([required]);
    }

    this.toggleControl(this.inputForm.controls.chipTime, controlTime.chipTime);
    this.toggleControl(this.inputForm.controls.time, controlTime.time);
    this.toggleControl(this.inputForm.controls.penaltyMinutes, true);
    this.toggleControl(this.inputForm.controls.lapDistancePassed, true);

    this.applyChanges = false;
    this.inputForm.patchValue({
      chipTime: controlTime.chipTime,
      time: controlTime.time,
      penaltyMinutes: controlTime.penaltyMinutes,
      lapDistancePassed: controlTime.lapDistancePassed
    });
    this.applyChanges = true;
  }

  toggleControl(control: FormControl, condition: any): void {
    condition ? control.enable({onlySelf: true, emitEvent: false}) : control.disable({onlySelf: true, emitEvent: false});
  }

  private resequenceLaps(times: ControlPointTime[]): ControlPointTime[] {
    const sorted = ControlPointTime.sortControlPointTimes([...times]);
    if (RaceSettings.isRoundRace(this.raceSettings?.resultProcessingType)) {
      let lap = 1;
      sorted.forEach(ct => {
        if (!ct.controlWaypoint && ct.lap) {
          ct.lap = lap++;
        }
      });
    }
    return sorted;
  }

  onApply(): void {
    if (!this.selectedSection || !this.selectedControlTime) return;
    this.applyRejected = false;

    const times = this.selectedSection.controlTimes;
    const time = this.inputForm.controls.time.value ?? undefined;
    const chipTime = this.inputForm.controls.chipTime.value ?? undefined;
    const penaltyMinutes = this.inputForm.controls.penaltyMinutes.value ?? undefined;
    const lapDistancePassed = this.inputForm.controls.lapDistancePassed.value ?? undefined;

    if (!time) {
      this.applyRejected = true;
      return;
    }

    const candidateRow: ControlPointTime = {...this.selectedControlTime, time, chipTime, penaltyMinutes, lapDistancePassed};
    const candidateTimes = this.resequenceLaps(
        times.map(cpt => cpt === this.selectedControlTime ? candidateRow : cpt)
    );

    if (!this.isRowValid(candidateRow, candidateTimes)) {
      this.applyRejected = true;
      return;
    }

    this.selectedControlTime.time = time;
    this.selectedControlTime.chipTime = chipTime;
    this.selectedControlTime.penaltyMinutes = penaltyMinutes;
    this.selectedControlTime.lapDistancePassed = lapDistancePassed;

    this.selectedSection.controlTimes = this.resequenceLaps(times);
    this.validateSection(this.selectedSection);
    this.updateSaveEnabled();
    this.recomputeDecidingRows();
  }

  private isRowValid(row: ControlPointTime, times: ControlPointTime[]): boolean {
    if (!row.id || !row.time) return true;

    const startDate = this.byWaypointType(times, WaypointType.START_WAYPOINT)?.time;
    const finishDate = this.byWaypointType(times, WaypointType.FINISH_WAYPOINT)?.time;
    const minimumLapDuration = RaceSettings.hasMinimumLapDuration(this.raceSettings?.minimumLapDuration)
        ? Number(this.raceSettings?.minimumLapDuration) || undefined : undefined;
    const distanceAlreadyInserted = this.raceSettings?.resultProcessingType === ResultProcessingType.TIME_LIMITED &&
        times.some(cpt => cpt.lapDistancePassed != null && cpt.lapDistancePassed > 0);

    const index = times.indexOf(row);
    const status = this.computeStatus(row, index, times, startDate, finishDate, minimumLapDuration, distanceAlreadyInserted);

    if (status !== CompetitorSaveControlPointResultStatus.OK) return false;
    return !(distanceAlreadyInserted && row.lapDistancePassed && this.raceSettings?.lapLength && row.lapDistancePassed > this.raceSettings.lapLength);
  }

  private computeStatus(
      cpt: ControlPointTime,
      index: number,
      times: ControlPointTime[],
      startDate: Date | undefined,
      finishDate: Date | undefined,
      minimumLapDuration: number | undefined,
      distanceAlreadyInserted: boolean
  ): CompetitorSaveControlPointResultStatus {
    const previous = index > 0 ? times.at(index - 1) : undefined;
    const waypointType = cpt.controlWaypoint?.waypointTypeId;
    let status = CompetitorSaveControlPointResultStatus.OK;

    if (waypointType === WaypointType.START_WAYPOINT && finishDate && cpt.time!.getTime() >= finishDate.getTime()) {
      status = CompetitorSaveControlPointResultStatus.AFTER_FINISH_TIME;
    } else if (waypointType === WaypointType.FINISH_WAYPOINT && startDate && cpt.time!.getTime() <= startDate.getTime()) {
      status = CompetitorSaveControlPointResultStatus.BEFORE_START_TIME;
    } else if (waypointType === WaypointType.CONTROL_WAYPOINT || cpt.lap != undefined) {
      if (startDate && cpt.time!.getTime() <= startDate.getTime()) {
        status = CompetitorSaveControlPointResultStatus.BEFORE_START_TIME;
      } else if (finishDate && cpt.time!.getTime() >= finishDate.getTime()) {
        status = CompetitorSaveControlPointResultStatus.AFTER_FINISH_TIME;
      }
      if (cpt.lap && minimumLapDuration && previous?.time) {
        const timeDifference = (cpt.time!.getTime() - previous.time.getTime()) / 1000;
        status = timeDifference < minimumLapDuration ? CompetitorSaveControlPointResultStatus.MINIMAL_LAP_DURATION_ERROR : status;
      }
      if (distanceAlreadyInserted && cpt.lapDistancePassed && (index < (times.length - 2))) {
        status = CompetitorSaveControlPointResultStatus.FINISH_TIME_BEFORE_OTHER;
      }
    }

    return status;
  }

  private validateSection(section: MemberSection): void {
    const times = section.controlTimes;
    const startDate = this.byWaypointType(times, WaypointType.START_WAYPOINT)?.time;
    const finishDate = this.byWaypointType(times, WaypointType.FINISH_WAYPOINT)?.time;
    let enableSave = true;
    const minimumLapDuration: number | undefined = RaceSettings.hasMinimumLapDuration(this.raceSettings?.minimumLapDuration) ?
        Number(this.raceSettings?.minimumLapDuration) || undefined : undefined;

    const distanceAlreadyInserted: boolean = this.raceSettings?.resultProcessingType === ResultProcessingType.TIME_LIMITED &&
        times.some(cpt => cpt.lapDistancePassed != null && cpt.lapDistancePassed > 0);

    times.forEach((cpt, i) => {
      if (!cpt.id || !cpt.time) return;

      const status = this.computeStatus(cpt, i, times, startDate, finishDate, minimumLapDuration, distanceAlreadyInserted);
      this._saveResult.set(cpt.id, status);

      if (status !== CompetitorSaveControlPointResultStatus.OK) {
        enableSave = false;
      }
      if (distanceAlreadyInserted && cpt.lapDistancePassed && this.raceSettings?.lapLength && cpt.lapDistancePassed > this.raceSettings.lapLength) {
        enableSave = false;
      }
    });

    this.sectionValidity.set(section, enableSave);
  }

  private updateSaveEnabled(): void {
    this.saveEnabled = this.sections.every(section => this.sectionValidity.get(section) === true);
  }

  disableFormControls(): void {
    this.inputForm.controls.chipTime.disable();
    this.inputForm.controls.time.disable();
    this.inputForm.controls.penaltyMinutes.disable();
    this.inputForm.controls.lapDistancePassed.disable();
  }

  onConfirmClick(): void {
    if (this.forTeamMembers) {
      this.sections.forEach(section => {
        if (section.member) {
          section.member.controlPointsTimes = section.controlTimes.map(
              cpt => ({...cpt, id: cpt.id === -1 ? undefined : cpt.id})
          );
        }
      });
    } else if (this.competitor) {
      this.competitor.controlPointsTimes = this.sections[0]?.controlTimes.map(
          cpt => ({...cpt, id: cpt.id === -1 ? undefined : cpt.id})
      );
    }
    this.onSave.emit(this.competitor);
  }

  onDismissClick(): void {
    this.activeModal.dismiss();
  }

  private mapHasChanged(newMap: Map<number, CompetitorSaveControlPointResultStatus>): boolean {
    if (this._saveResult.size !== newMap.size) return true;
    for (const [key, value] of newMap.entries()) {
      if (this._saveResult.get(key) !== value) return true;
    }
    return false;
  }

  protected formControls = Object.entries(this.inputForm.controls);

  protected readonly WaypointType = WaypointType;
  protected readonly CompetitorSaveControlPointResultStatus = CompetitorSaveControlPointResultStatus;
  protected readonly ResultProcessingType = ResultProcessingType;
}